/**
 * The ambient-volume slider — the loudness lever beside the Sound toggle
 * (D116; D117 gave the layer a perceptual taper and this control a readout).
 *
 * @remarks
 * The value this control binds is the SETTING — the handle's position, 0..1
 * — and that is also exactly what the store persists, so what the player set
 * is what the slider shows, forever, with no mapping to drift through
 * (Mike's D117 finding: the earlier gain-storing version lost a little truth
 * on every round trip). The perceptual work happens downstream: the web
 * renderer cubes the setting into a gain (loudness perception is roughly
 * logarithmic; the cube is the standard cheap approximation), so the quiet
 * defaults still sit a third of the way along the track and every arrow
 * press changes something audible.
 *
 * The percent readout shows the SETTING (31% means a third of the track,
 * not a third of full scale) — it names what the handle says, in the only
 * frame the handle has. `aria-hidden` because the slider itself already
 * announces its value to assistive tech; a second announcement of the same
 * number would be noise.
 *
 * A native `<input type="range">`: keyboard-adjustable, screen-reader
 * labeled, styled by the shell — the component owns behavior, not chrome.
 *
 * @packageDocumentation
 */

/** Props for {@link AmbientVolumeSliderComponent}. */
export interface AmbientVolumeSliderComponentProps {
	/** Called with the SETTING the player is asking for, 0..1. */
	readonly onVolumeChange: (volume: number) => void;
	/** The current setting (handle position), 0..1. */
	readonly volume: number;
}

/**
 * Render the ambience loudness slider with its percent readout.
 *
 * @param props - {@link AmbientVolumeSliderComponentProps}
 * @returns The slider
 */
export const AmbientVolumeSliderComponent = ({ onVolumeChange, volume }: AmbientVolumeSliderComponentProps) => (
	<label className="ambient-volume-slider">
		<span className="ambient-volume-slider__label">Vol</span>
		<input
			aria-label="Ambient volume"
			className="ambient-volume-slider__input"
			max={1}
			min={0}
			onChange={(changeEvent) => {
				onVolumeChange(Number(changeEvent.target.value));
			}}
			step={0.01}
			type="range"
			value={volume}
		/>
		<span aria-hidden="true" className="ambient-volume-slider__readout">
			{Math.round(volume * 100)}%
		</span>
	</label>
);
